import React, { useEffect, useState } from 'react';
import { getReviews, saveReview } from '../services/apiService';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Star, 
  Quote, 
  MessageCircle, 
  CheckCircle2, 
  Play,
  Globe,
  X,
  Send,
  Loader2
} from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [videoReviews, setVideoReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    text: '',
    course: '',
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getReviews();
        // Filter out reviews with blob URLs
        const validReviews = data.filter(review => {
          const url = review.photo || review.photo_url || review.imageUrl;
          return !url || !url.startsWith('blob:');
        });
        
        // Separate video reviews and regular reviews
        const videos = validReviews.filter(r => r.is_video_review && r.video_url);
        const regular = validReviews.filter(r => !r.is_video_review);
        
        setVideoReviews(videos);
        setReviews(regular);
      } catch (error) {
        console.error("Failed to fetch reviews", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get YouTube thumbnail from URL
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }
    return null;
  };

  // Handle review form submission
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await saveReview({
        ...formData,
        is_public: true,
        is_video_review: false,
      });
      setShowReviewForm(false);
      setFormData({ name: '', email: '', rating: 5, text: '', course: '' });
      
      // Reload reviews
      const data = await getReviews();
      const validReviews = data.filter(review => {
        const url = review.photo || review.photo_url || review.imageUrl;
        return !url || !url.startsWith('blob:');
      });
      const regular = validReviews.filter(r => !r.is_video_review);
      setReviews(regular);
      
      alert('Thank you for your review! It will be visible after admin approval.');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Render stars
  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i} 
            size={14} 
            className={`${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200 fill-slate-200'}`} 
          />
        ))}
      </div>
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayer
          videoUrl={selectedVideo.video_url}
          title={selectedVideo.name}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Write a Review</h2>
              <button
                onClick={() => setShowReviewForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Rating *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating })}
                      className={`p-2 rounded-lg transition-all ${
                        formData.rating >= rating
                          ? 'text-yellow-400'
                          : 'text-slate-300'
                      }`}
                    >
                      <Star size={32} fill={formData.rating >= rating ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Course (Optional)
                </label>
                <input
                  type="text"
                  value={formData.course}
                  onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                  placeholder="e.g., IELTS, OET, PTE"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Your Review *
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  placeholder="Share your experience..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Review
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  className="px-6 py-3 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-[100px] pointer-events-none translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="text-center md:text-left">
            <h1 className="text-4xl text-gray-100 font-serif font-bold mb-6">
              Loved by <span className="text-red-500">Students.</span> <br/>
              Trusted by Parents.
            </h1>
            <p className="text-slate-400 text-lg max-w-xl leading-relaxed">
              We take pride in our 100% satisfaction rate. Read honest feedback from students who have walked these halls.
            </p>
          </div>

          <div className="bg-white text-slate-900 p-8 rounded-3xl shadow-2xl flex flex-col items-center min-w-[280px]">
            <div className="text-6xl font-bold text-slate-900 mb-2">4.9</div>
            <div className="flex gap-1 mb-4 text-yellow-400">
              {[1,2,3,4,5].map(i => <Star key={i} size={24} fill="currentColor" />)}
            </div>
            <div className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4">Average Rating</div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-xs font-bold text-slate-600">
              <Globe size={14} /> Based on Reviews
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      {videoReviews.length > 0 && (
        <section className="py-16 md:py-24 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl font-serif font-bold text-slate-900">Watch their stories</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videoReviews.map((video) => {
                const thumbnail = getYouTubeThumbnail(video.video_url);
                return (
                  <div
                    key={video.id}
                    onClick={() => setSelectedVideo(video)}
                    className="group relative rounded-2xl overflow-hidden aspect-video cursor-pointer shadow-lg"
                  >
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={video.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x450?text=Video';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                        <Play size={48} className="text-slate-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/50 group-hover:scale-110 transition-transform">
                        <Play size={24} fill="currentColor" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 text-white font-bold text-sm">
                      {video.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Reviews Grid Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-serif font-bold text-slate-900">Latest Reviews</h2>
            <button
              onClick={() => setShowReviewForm(true)}
              className="px-6 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-all"
            >
              Write a Review
            </button>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-200 rounded-2xl animate-pulse"/>)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.length > 0 ? reviews.map((review, i) => (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  key={review.id || i}
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-red-100 transition-all duration-300 flex flex-col"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0 text-lg font-bold text-slate-400">
                      {(() => {
                        const url = review.photo || review.photo_url || review.imageUrl;
                        const safeUrl = url && !url.startsWith('blob:') ? url : null;
                        return safeUrl ? (
                          <img 
                            src={safeUrl} 
                            alt={review.name} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null;
                      })()}
                      <span style={{ display: (() => {
                        const url = review.photo || review.photo_url || review.imageUrl;
                        return url && !url.startsWith('blob:') ? 'none' : 'flex';
                      })() }}>
                        {(review.name || "S").charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">{review.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {renderStars(review.rating || 5)}
                      </div>
                    </div>
                    <div className="ml-auto text-slate-300">
                      <Quote size={20} />
                    </div>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-1">
                    "{review.text || review.review || "The training was exceptional. I highly recommend 6th Sense to anyone looking for serious results."}"
                  </p>

                  <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      {review.course || "Alumni"}
                    </span>
                    <div className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
                      <CheckCircle2 size={12} /> Verified
                    </div>
                  </div>
                </motion.div>
              )) : (
                <div className="col-span-full text-center py-20">
                  <MessageCircle size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No reviews loaded yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-slate-100 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-bold text-slate-900 mb-6">Want results like these?</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-free-counselling"
              className="px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all shadow-xl shadow-red-900/20"
            >
              Start Your Journey
            </Link>
            <button
              onClick={() => setShowReviewForm(true)}
              className="px-8 py-4 bg-slate-50 text-slate-700 font-bold rounded-xl hover:bg-slate-100 border border-slate-200 transition-all"
            >
              Write a Review
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
